import { Component, Inject, OnInit, ViewChild, ElementRef, OnDestroy, AfterViewInit, ChangeDetectorRef, HostListener } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as pdfjsLib from 'pdfjs-dist';
import Swal from 'sweetalert2';
import { EntityUpdate } from '../scans.interface';
import { ScansService } from '../scans.service';
import { CopiiService } from '../../copii/copii.service';
import { helper } from 'src/environments/helper';
import { catchError, distinctUntilChanged, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { FormControl } from '@angular/forms';
import { DocumentTypeService } from 'src/app/platform/nomenclatoare/activities-docs-setari/document-type/document-type.service';

pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

interface PageGroup {
    id: string;
    name: string;
    pages: number[];
    metadata: any;
    expanded?: boolean;
}

interface PageMetadata {
    pageNumber: number;
    metadata: any;
}

@Component({
    selector: 'app-pdf-details',
    templateUrl: './pdf-details.component.html',
    styleUrls: ['./pdf-details.component.scss']
})
export class PdfDetailsComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('pdfCanvas', { static: false }) canvas!: ElementRef<HTMLCanvasElement>;
    @ViewChild('canvasContainer', { static: false }) canvasContainer!: ElementRef;

    // PDF related properties
    pdfDocument: any = null;
    currentPage = 1;
    totalPages = 0;
    zoomLevel = 1.0;
    zoomPercentage = 100;
    rotation = 0;
    canDelete = true;
    helper = helper;
    isLoading = false;

    // UI state
    activeTabIndex = 0;
    isSelectionMode = false;
    selectedPages: number[] = [];
    showGroupModal = false;
    newGroupName = '';
    viewMode: 'single' | 'grid' = 'single';

    editingGroup: any = null;
    isEditMode = false;
    showEditGroupModal = false;

    // Rendering control
    private currentRenderTask: any = null;
    private renderDebounceTimer: any = null;
    private thumbnailCache: Map<number, string> = new Map();
    private thumbnailRenderQueue: number[] = [];
    private isRenderingThumbnails = false;

    // Data structures
    pageGroups: PageGroup[] = [];
    pageMetadata: PageMetadata[] = [];
    pagesList: number[] = [];

    // Proprietăți pentru entitate
    entitiesList: any[] = [];

    // Proprietăți pentru management
    selectedPriority!: number;
    selectedStatus!: number;
    selectedOperator!: number;
    selectedEntityId!: number;

    // Valorile originale pentru comparație
    originalPriority: number | null = null;
    originalStatus: number | null = null;
    originalOperator: number | null = null;
    originalEntityId: number | null = null;

    // Proprietăți pentru gestionarea grupurilor de documente
    selectedDocumentGroup: any = null;
    documentGroups: any[] = [];

    // Chip input configuration
    separatorKeysCodes: number[] = [13, 188]; // Enter and comma

    documentPriorities: any[] = [];
    documentStatuses: any[] = [];
    operatori: any[] = [];
    documentTypes: any[] = [];

    // Proprietăți pentru editarea numelui
    showEditNameModal = false;
    editingDocumentName = '';
    originalDocumentName = '';
    isSavingName = false;
    expandedGroupId: number | null = null;

    private destroy$ = new Subject<void>();

    // Filtered fields for search
    filteredEntitati!: any[];
    entitatiControl = new FormControl();

    filteredTipuriDocument!: any[];
    tipuriDocumentControl = new FormControl();

    filteredEntitatiGrup!: any[];
    entitatiGrupControl = new FormControl();

    filteredOperatori!: any[];
    operatoriControl = new FormControl();

    constructor(
        public dialogRef: MatDialogRef<PdfDetailsComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private cdr: ChangeDetectorRef,
        private scansService: ScansService,
        private documentTypeService: DocumentTypeService
    ) { }

    ngOnInit() {
        this.loadPdf();
        this.initializeManagementData();
        this.initializeDocumentGroups();
        this.initializeDocumentName();
        this.renderFieldsSearch();
        this.loadDocumentTypes();
        console.log(this.data.rowData);
    }

    ngAfterViewInit() {
        setTimeout(() => {
            if (this.pdfDocument && this.viewMode === 'single') {
                this.renderPage(this.currentPage, true);
            }
        }, 100);
    }

    ngOnDestroy() {
        this.cleanupResources();

        this.destroy$.next();
        this.destroy$.complete();
    }

    private cleanupResources() {
        if (this.currentRenderTask) {
            try {
                this.currentRenderTask.cancel();
            } catch (error) {
                console.log('Error cancelling render task:', error);
            }
            this.currentRenderTask = null;
        }

        if (this.renderDebounceTimer) {
            clearTimeout(this.renderDebounceTimer);
            this.renderDebounceTimer = null;
        }

        this.isRenderingThumbnails = false;
        this.thumbnailRenderQueue = [];
        this.thumbnailCache.clear();

        if (this.pdfDocument) {
            try {
                this.pdfDocument.destroy();
            } catch (error) {
                console.log('Error destroying PDF document:', error);
            }
            this.pdfDocument = null;
        }

        if (this.data.pdf.url && this.data.pdf.blob) {
            try {
                URL.revokeObjectURL(this.data.pdf.url);
            } catch (error) {
                console.log('Error revoking PDF URL:', error);
            }
        }
    }

    // Inițializează grupurile de documente din rowData
    private initializeDocumentGroups(): void {
        if (this.data.rowData?.document_groups) {
            this.documentGroups = this.data.rowData.document_groups;
            console.log('Document groups loaded:', this.documentGroups);
        }
    }

    // Verifică dacă documentul are entitate asignată
    get hasAssignedEntity(): boolean {
        return this.data.rowData?.entity_id != null && this.data.rowData?.entity != null;
    }

    // Obține datele entității
    get entityData(): any {
        return this.data.rowData?.entity || null;
    }

    // Verifică dacă butonul de salvare ar trebui să fie vizibil
    get shouldShowSaveButton(): boolean {
        return this.activeTabIndex === 1; // Tab-ul de Metadate (index 1)
    }

    // Inițializează datele de management
    private initializeManagementData(): void {
        if (this.data.rowData) {
            // Setează valorile curente din rowData
            this.selectedPriority = this.data.rowData.priority_id || null;
            this.selectedStatus = this.data.rowData.status_id || null;
            this.selectedOperator = this.data.rowData.assigned_to_user_id || null;
            this.selectedEntityId = this.data.rowData.entity_id || null;

            // Salvează valorile originale pentru comparație
            this.originalPriority = this.selectedPriority;
            this.originalStatus = this.selectedStatus;
            this.originalOperator = this.selectedOperator;
            this.originalEntityId = this.selectedEntityId;
        }

        // Inițializează listele din data
        this.entitiesList = this.data.entitiesList || [];
        this.filteredEntitati = this.entitiesList;
        this.filteredEntitatiGrup = this.entitiesList;
        this.documentPriorities = this.data.documentPriorities || [];
        this.documentStatuses = this.data.documentStatuses || [];
        this.operatori = this.data.operatori || [];
        this.filteredOperatori = this.data.operatori;
    }

    // Event handlers pentru schimbările din dropdown-uri
    onPriorityChange(): void {
        console.log('Priority changed to:', this.selectedPriority);
    }

    onStatusChange(): void {
        console.log('Status changed to:', this.selectedStatus);
    }

    onOperatorChange(): void {
        console.log('Operator changed to:', this.selectedOperator);
    }

    onEntityChange(): void {
        console.log('Entity changed to:', this.selectedEntityId);
    }

    // Verifică dacă există modificări în management
    hasManagementChanges(): boolean {
        return this.selectedPriority !== this.originalPriority ||
            this.selectedStatus !== this.originalStatus ||
            this.selectedOperator !== this.originalOperator ||
            this.selectedEntityId !== this.originalEntityId;
    }

    // Salvează toate modificările de management
    saveManagementChanges(): void {
        if (!this.hasManagementChanges()) {
            return;
        }

        const updatePromises: Promise<any>[] = [];

        // Verifică și adaugă update-uri pentru fiecare câmp modificat
        if (this.selectedPriority !== this.originalPriority) {
            console.log('Updating priority from', this.originalPriority, 'to', this.selectedPriority);
            updatePromises.push(this.updateDocumentPriority());
        }

        if (this.selectedStatus !== this.originalStatus) {
            console.log('Updating status from', this.originalStatus, 'to', this.selectedStatus);
            updatePromises.push(this.updateDocumentStatus());
        }

        if (this.selectedOperator !== this.originalOperator) {
            console.log('Updating operator from', this.originalOperator, 'to', this.selectedOperator);
            updatePromises.push(this.updateDocumentOperator());
        }

        if (this.selectedEntityId !== this.originalEntityId) {
            console.log('Updating entity from', this.originalEntityId, 'to', this.selectedEntityId);
            updatePromises.push(this.updateDocumentEntity());
        }

        // Activează loading
        this.isLoading = true;

        // Execută toate update-urile în paralel
        Promise.all(updatePromises)
            .then((results) => {
                console.log('All management updates completed successfully:', results);

                // Actualizează valorile originale
                this.originalPriority = this.selectedPriority;
                this.originalStatus = this.selectedStatus;
                this.originalOperator = this.selectedOperator;
                this.originalEntityId = this.selectedEntityId;

                // Dezactivează loading
                this.isLoading = false;

                // Afișează mesaj de succes
                Swal.fire({
                    icon: 'success',
                    title: 'Succes',
                    text: 'Modificările au fost salvate cu succes.',
                    confirmButtonText: 'OK'
                }).then(() => {
                    // Reîmprospătează datele documentului
                    this.refreshDocumentData();
                });
            })
            .catch((error) => {
                console.error('Error during management updates:', error);

                // Dezactivează loading
                this.isLoading = false;

                // Afișează mesaj de eroare
                Swal.fire({
                    icon: 'error',
                    title: 'Eroare',
                    text: 'Au apărut erori la salvarea modificărilor. Vă rugăm să încercați din nou.',
                    confirmButtonText: 'OK'
                });
            });
    }

    // Funcții individuale pentru update-uri
    private updateDocumentPriority(): Promise<any> {
        return new Promise((resolve, reject) => {
            const payload = {
                document_id: this.data.rowData.id,
                priority_id: this.selectedPriority
            };

            this.scansService.updatePriority(payload).subscribe({
                next: (response) => {
                    console.log('Priority updated successfully:', response);
                    resolve(response);
                },
                error: (error) => {
                    console.error('Error updating priority:', error);
                    reject(error);
                }
            });
        });
    }

    private updateDocumentStatus(): Promise<any> {
        return new Promise((resolve, reject) => {
            const payload = {
                document_id: this.data.rowData.id,
                status_id: this.selectedStatus
            };

            this.scansService.updateStatus(payload).subscribe({
                next: (response) => {
                    console.log('Status updated successfully:', response);
                    resolve(response);
                },
                error: (error) => {
                    console.error('Error updating status:', error);
                    reject(error);
                }
            });
        });
    }

    private updateDocumentOperator(): Promise<any> {
        return new Promise((resolve, reject) => {
            const payload = {
                document_id: this.data.rowData.id,
                user_id: this.selectedOperator
            };

            this.scansService.updateOperator(payload).subscribe({
                next: (response) => {
                    console.log('Operator updated successfully:', response);
                    resolve(response);
                },
                error: (error) => {
                    console.error('Error updating operator:', error);
                    reject(error);
                }
            });
        });
    }

    private updateDocumentEntity(): Promise<any> {
        return new Promise((resolve, reject) => {
            const payload: EntityUpdate = {
                entity_id: this.selectedEntityId,
                document_id: this.data.rowData.id
            };

            this.scansService.updateEntity(payload).subscribe({
                next: (response) => {
                    console.log('Entity updated successfully:', response);
                    resolve(response);
                },
                error: (error) => {
                    console.error('Error updating entity:', error);
                    reject(error);
                }
            });
        });
    }


    // Reîmprospătează datele documentului
    private refreshDocumentData(): void {
        if (!this.data.rowData?.id) {
            console.error('No document ID available for refresh');
            return;
        }

        console.log('Refreshing document data for ID:', this.data.rowData.id);

        const selectedGroupId = this.selectedDocumentGroup?.id;
        this.isLoading = true;

        this.scansService.getEntity(this.data.rowData.id).subscribe({
            next: (response: any) => {
                try {
                    if (!response.errors && response.data) {
                        console.log('Document data refreshed successfully:', response.data);

                        const documentData = response.data as any;
                        this.data.rowData = documentData;

                        // Actualizează valorile de management
                        this.selectedPriority = documentData.id_priority || null;
                        this.selectedStatus = documentData.id_status || null;
                        this.selectedOperator = documentData.assigned_to_user_id || null;
                        this.selectedEntityId = documentData.entity_id || null;

                        // Actualizează valorile originale
                        this.originalPriority = this.selectedPriority;
                        this.originalStatus = this.selectedStatus;
                        this.originalOperator = this.selectedOperator;
                        this.originalEntityId = this.selectedEntityId;
                        this.entitatiControl.setValue('');
                        this.tipuriDocumentControl.setValue('');
                        this.entitatiGrupControl.setValue('');
                        this.operatoriControl.setValue('');

                        // Actualizează grupurile și metadatele
                        if (documentData.document_groups) {
                            this.documentGroups = documentData.document_groups;
                            console.log('Document groups updated:', this.documentGroups);

                            if (selectedGroupId) {
                                this.selectedDocumentGroup = this.documentGroups.find(group => group.id === selectedGroupId) || null;
                            }
                        }

                        this.cdr.detectChanges();

                        console.log('UI updated with refreshed data');

                        Swal.fire({
                            icon: 'success',
                            title: 'Actualizare completă',
                            text: 'Datele documentului au fost actualizate cu succes.',
                            timer: 2000,
                            timerProgressBar: true,
                            showConfirmButton: false,
                            toast: true,
                            position: 'top-end'
                        });
                    } else {
                        console.error('Error in response:', response);
                        this.showRefreshError();
                    }
                } catch (error) {
                    console.error('Error processing refresh response:', error);
                    this.showRefreshError();
                } finally {
                    this.isLoading = false;
                    this.cdr.detectChanges();
                }
            },
            error: (error: any) => {
                console.error('Error refreshing document data:', error);
                this.showRefreshError();
                this.isLoading = false;
                this.cdr.detectChanges();
            }
        });
    }

    // Funcție helper pentru afișarea erorii de refresh
    private showRefreshError(): void {
        Swal.fire({
            icon: 'error',
            title: 'Eroare la actualizare',
            text: 'Nu s-au putut reîmprospăta datele documentului. Unele informații afișate pot fi învechite.',
            confirmButtonText: 'OK',
            timer: 5000,
            timerProgressBar: true
        });
    }

    // Gestionarea schimbării grupului de document
    onDocumentGroupChange(): void {
        console.log('Document group changed to:', this.selectedDocumentGroup);
        if (this.selectedDocumentGroup) {
            this.loadDocumentGroupMetadata();
        }

        // Întotdeauna resetează controlul la schimbarea grupului
        this.resetGrupSearchControl();
    }
    // Încarcă metadatele pentru grupul de document selectat
    private loadDocumentGroupMetadata(): void {
        if (!this.selectedDocumentGroup) return;

        console.log('Loaded metadata for group:', this.selectedDocumentGroup);
    }

    // Salvează metadatele grupului de document
    saveDocumentGroupMetadata(): void {
        if (!this.selectedDocumentGroup) return;

        console.log('Saving document group metadata:', this.selectedDocumentGroup);

        // Implementează apelul API pentru salvarea metadatelor grupului
        const payload = {
            id: this.selectedDocumentGroup.id,
            title: this.selectedDocumentGroup.title,
            description: this.selectedDocumentGroup.description,
            notes: this.selectedDocumentGroup.notes,
            entity_id: this.selectedDocumentGroup.entity_id,
            id_document_type: this.selectedDocumentGroup.document_type_id
        };

        // Exemplu de apel serviciu (ajustează în funcție de serviciul tău)
        // this.scansService.updateDocumentGroupMetadata(payload).subscribe({
        //     next: (response) => {
        //         console.log('Document group metadata updated successfully');
        //         Swal.fire({
        //             icon: 'success',
        //             title: 'Succes',
        //             text: 'Metadatele grupului au fost salvate cu succes.',
        //             confirmButtonText: 'OK'
        //         });
        //     },
        //     error: (error) => {
        //         console.error('Error updating document group metadata:', error);
        //         Swal.fire({
        //             icon: 'error',
        //             title: 'Eroare',
        //             text: 'Nu s-au putut salva metadatele grupului. Vă rugăm să încercați din nou.',
        //             confirmButtonText: 'OK'
        //         });
        //     }
        // });
    }

    // Metode pentru selecție multiple
    selectAllPages(): void {
        this.selectedPages = [...this.pagesList];
    }

    deselectAllPages(): void {
        this.selectedPages = [];
    }

    toggleSelectAll(): void {
        if (this.areAllAvailablePagesSelected()) {
            // Deselectează toate paginile disponibile
            this.selectedPages = this.selectedPages.filter(page => !this.canSelectPage(page));
        } else {
            // Selectează toate paginile disponibile
            const availablePages = this.pagesList.filter(page => this.canSelectPage(page));
            this.selectedPages = [...new Set([...this.selectedPages, ...availablePages])];
        }
        this.selectedPages.sort((a, b) => a - b);
    }

    // Verifică dacă toate paginile disponibile sunt selectate
    areAllAvailablePagesSelected(): boolean {
        const availablePages = this.pagesList.filter(page => this.canSelectPage(page));
        return availablePages.length > 0 && availablePages.every(page => this.selectedPages.includes(page));
    }

    areAllPagesSelected(): boolean {
        return this.selectedPages.length === this.totalPages && this.totalPages > 0;
    }

    getSelectAllButtonText(): string {
        if (this.selectedPages.length === 0) {
            return 'Selectează Tot';
        } else if (this.areAllPagesSelected()) {
            return 'Deselectează Tot';
        } else {
            return `Selectează Tot (${this.selectedPages.length}/${this.totalPages})`;
        }
    }

    getSelectAllButtonIcon(): string {
        return this.areAllPagesSelected() ? 'clear_all' : 'select_all';
    }

    // Verifică dacă o pagină specifică este selectată
    isPageSelected(pageNum: number): boolean {
        return this.selectedPages.includes(pageNum);
    }

    async loadPdf() {
        try {
            if (typeof pdfjsLib === 'undefined') {
                throw new Error('PDF.js library not loaded. Please check angular.json configuration.');
            }

            console.log('Loading PDF:', this.data.pdf.url);
            this.pdfDocument = await pdfjsLib.getDocument(this.data.pdf.url).promise;
            this.totalPages = this.pdfDocument.numPages;
            this.pagesList = Array.from({ length: this.totalPages }, (_, i) => i + 1);

            console.log('PDF loaded successfully. Total pages:', this.totalPages);

            if (this.viewMode === 'single') {
                await this.renderPage(this.currentPage, true);
            } else {
                this.generateThumbnails();
            }
        } catch (error) {
            console.error('Error loading PDF:', error);
            alert('Eroare la încărcarea PDF-ului. Vă rugăm să încercați din nou.');
        }
    }

    async renderPage(pageNum: number, immediate: boolean = false) {
        if (!this.pdfDocument || !this.canvas || this.viewMode !== 'single') return;

        if (this.currentRenderTask) {
            this.currentRenderTask.cancel();
            this.currentRenderTask = null;
        }

        if (this.renderDebounceTimer) {
            clearTimeout(this.renderDebounceTimer);
            this.renderDebounceTimer = null;
        }

        if (!immediate) {
            this.renderDebounceTimer = setTimeout(() => {
                this.performRender(pageNum);
            }, 100);
            return;
        }

        await this.performRender(pageNum);
    }

    private async performRender(pageNum: number) {
        if (!this.pdfDocument || !this.canvas) return;

        try {
            const page = await this.pdfDocument.getPage(pageNum);
            const canvas = this.canvas.nativeElement;
            const context = canvas.getContext('2d');

            let viewport = page.getViewport({ scale: this.zoomLevel, rotation: this.rotation });
            canvas.height = viewport.height;
            canvas.width = viewport.width;

            const renderContext = {
                canvasContext: context,
                viewport: viewport
            };

            this.currentRenderTask = page.render(renderContext);
            await this.currentRenderTask.promise;
            this.currentRenderTask = null;
        } catch (error: any) {
            if (error.name === 'RenderingCancelledException') {
                console.log('Render cancelled for page:', pageNum);
            } else {
                console.error('Error rendering page:', error);
            }
        }
    }

    onViewModeChange() {
        console.log('View mode changed to:', this.viewMode);

        if (this.viewMode === 'grid') {
            setTimeout(() => {
                this.generateThumbnails();
            }, 100);
        } else {
            setTimeout(() => {
                if (this.canvas) {
                    this.renderPage(this.currentPage, true);
                }
            }, 100);
        }
    }

    async generateThumbnails() {
        if (!this.pdfDocument || this.viewMode !== 'grid') return;

        this.thumbnailRenderQueue = [...this.pagesList];
        this.isRenderingThumbnails = true;
        this.processThumbnailQueue();
    }

    private async processThumbnailQueue() {
        if (this.thumbnailRenderQueue.length === 0 || !this.isRenderingThumbnails) {
            this.isRenderingThumbnails = false;
            return;
        }

        const pageNum = this.thumbnailRenderQueue.shift()!;

        try {
            await this.renderThumbnail(pageNum);
        } catch (error) {
            console.error(`Error processing thumbnail ${pageNum}:`, error);
        }

        if (this.isRenderingThumbnails) {
            setTimeout(() => {
                this.processThumbnailQueue();
            }, 50);
        }
    }

    private async renderThumbnail(pageNum: number) {
        if (!this.pdfDocument || !this.isRenderingThumbnails) return;

        try {
            const page = await this.pdfDocument.getPage(pageNum);
            const canvas = document.getElementById(`thumbnail-${pageNum}`) as HTMLCanvasElement;

            if (!canvas || !this.isRenderingThumbnails) {
                return;
            }

            const context = canvas.getContext('2d');
            if (!context) return;

            const viewport = page.getViewport({ scale: 0.5 });
            canvas.height = viewport.height;
            canvas.width = viewport.width;

            const renderContext = {
                canvasContext: context,
                viewport: viewport
            };

            const renderPromise = page.render(renderContext).promise;
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Render timeout')), 5000);
            });

            await Promise.race([renderPromise, timeoutPromise]);

            if (this.isRenderingThumbnails) {
                this.thumbnailCache.set(pageNum, canvas.toDataURL());
            }

        } catch (error) {
            console.error(`Error generating thumbnail for page ${pageNum}:`, error);
        }
    }

    // Navigation methods
    previousPage() {
        if (this.currentPage > 1) {
            this.currentPage--;
            if (this.viewMode === 'single') {
                this.renderPage(this.currentPage, true);
            }
        }
    }

    nextPage() {
        if (this.currentPage < this.totalPages) {
            this.currentPage++;
            if (this.viewMode === 'single') {
                this.renderPage(this.currentPage, true);
            }
        }
    }

    goToPage(pageNum: number) {
        if (pageNum >= 1 && pageNum <= this.totalPages) {
            this.currentPage = pageNum;

            if (this.viewMode === 'grid') {
                this.viewMode = 'single';
                setTimeout(() => {
                    this.renderPage(this.currentPage, true);
                }, 100);
            } else {
                this.renderPage(this.currentPage, true);
            }
        }
    }

    onPageThumbnailClick(pageNum: number) {
        if (this.isSelectionMode) {
            this.togglePageSelection(pageNum);
        } else {
            this.goToPage(pageNum);
        }
    }

    // Group management methods
    isCurrentPageSelected(): boolean {
        return this.selectedPages.includes(this.currentPage);
    }

    toggleCurrentPageInGroup() {
        const index = this.selectedPages.indexOf(this.currentPage);
        if (index > -1) {
            this.selectedPages.splice(index, 1);
        } else {
            this.selectedPages.push(this.currentPage);
            this.selectedPages.sort((a, b) => a - b);
        }
    }

    // Zoom and rotation methods
    zoomIn() {
        this.zoomPercentage = Math.min(this.zoomPercentage + 5, 300);
        this.zoomLevel = this.zoomPercentage / 100;
        if (this.viewMode === 'single') {
            this.renderPage(this.currentPage, true);
        }
    }

    zoomOut() {
        this.zoomPercentage = Math.max(this.zoomPercentage - 5, 25);
        this.zoomLevel = this.zoomPercentage / 100;
        if (this.viewMode === 'single') {
            this.renderPage(this.currentPage, true);
        }
    }

    onZoomSliderChange(event: any) {
        this.zoomPercentage = parseInt(event.target.value);
        this.zoomLevel = this.zoomPercentage / 100;
        if (this.viewMode === 'single') {
            this.renderPage(this.currentPage);
        }
    }

    onZoomInputChange() {
        this.zoomPercentage = Math.max(25, Math.min(300, this.zoomPercentage));
        this.zoomLevel = this.zoomPercentage / 100;
        if (this.viewMode === 'single') {
            this.renderPage(this.currentPage, true);
        }
    }

    fitToWidth() {
        if (this.canvasContainer && this.viewMode === 'single') {
            const containerWidth = this.canvasContainer.nativeElement.clientWidth - 40;
            const currentCanvasWidth = this.canvas.nativeElement.width / this.zoomLevel;
            this.zoomLevel = containerWidth / currentCanvasWidth;
            this.zoomPercentage = Math.round(this.zoomLevel * 100);
            this.zoomPercentage = Math.max(25, Math.min(300, this.zoomPercentage));
            this.zoomLevel = this.zoomPercentage / 100;
            this.renderPage(this.currentPage, true);
        }
    }

    rotateLeft() {
        this.rotation = (this.rotation - 90) % 360;
        if (this.viewMode === 'single') {
            this.renderPage(this.currentPage, true);
        } else {
            this.generateThumbnails();
        }
    }

    rotateRight() {
        this.rotation = (this.rotation + 90) % 360;
        if (this.viewMode === 'single') {
            this.renderPage(this.currentPage, true);
        } else {
            this.generateThumbnails();
        }
    }

    // Page selection methods
    startSelection() {
        this.isSelectionMode = true;
        this.selectedPages = [];
    }

    togglePageSelection(pageNum: number) {
        if (!this.isSelectionMode) return;

        const index = this.selectedPages.indexOf(pageNum);
        if (index > -1) {
            this.selectedPages.splice(index, 1);
        } else {
            this.selectedPages.push(pageNum);
        }
        this.selectedPages.sort((a, b) => a - b);
    }

    cancelSelection() {
        this.isSelectionMode = false;
        this.selectedPages = [];
    }

    createGroupFromSelection() {
        if (this.selectedPages.length === 0) return;

        if (this.isEditMode) {
            // Modul editare - deschide modal pentru editare
            this.showEditGroupModal = true;
        } else {
            // Modul creare - deschide modal pentru creare
            this.showGroupModal = true;
            // this.newGroupName = `Grup ${this.documentGroups.length + 1}`;
            this.newGroupName = '';
        }
    }

    confirmGroupCreation() {
        if (!this.newGroupName.trim()) return;

        // Calculează page_start și page_end din paginile selectate
        const sortedPages = [...this.selectedPages].sort((a, b) => a - b);

        // Creează payload-ul pentru API
        const payload = {
            document_oficial: this.data.rowData.id, // ID-ul documentului
            pages: sortedPages, // Paginile selectate
            total_pages_in_group: sortedPages.length,
            title: this.newGroupName
        };

        console.log('Creating new group:', payload);

        // Activează loading (opțional)
        this.isLoading = true;

        // Fă API call-ul pentru crearea grupului
        this.scansService.storeGroup(payload).subscribe({
            next: (response) => {
                console.log('Group created successfully:', response);

                // Dezactivează loading
                this.isLoading = false;

                // Afișează mesaj de succes
                Swal.fire({
                    icon: 'success',
                    title: 'Succes',
                    text: 'Grupul a fost creat cu succes.',
                    confirmButtonText: 'OK'
                }).then(() => {
                    // Reîmprospătează datele pentru a vedea noul grup
                    this.refreshDocumentData();

                    // Închide modalul și resetează selecția
                    this.cancelGroupCreation();
                    this.cancelSelection();
                });
            },
            error: (error) => {
                console.log('Error creating group:', error);

                // Dezactivează loading
                this.isLoading = false;

                // Afișează mesaj de eroare
                Swal.fire({
                    icon: 'error',
                    title: 'Eroare',
                    text: `${error.error.errors.message[0]}`,
                    confirmButtonText: 'OK'
                });
            }
        });
    }

    cancelGroupCreation() {
        this.showGroupModal = false;
        this.newGroupName = '';
    }

    // Confirmă editarea grupului
    confirmEditGroup() {
        if (!this.editingGroup || this.selectedPages.length === 0) return;

        const payload = {
            group_id: this.editingGroup.id,
            pages: [...this.selectedPages],
            total_pages_in_group: this.selectedPages.length
        };

        console.log('Updating group:', payload);

        // Activează loading
        this.isLoading = true;

        // Fă API call-ul pentru editarea grupului
        this.scansService.updateGroup(payload).subscribe({
            next: (response) => {
                console.log('Group updated successfully:', response);

                // Dezactivează loading
                this.isLoading = false;

                // Afișează mesaj de succes
                Swal.fire({
                    icon: 'success',
                    title: 'Succes',
                    text: 'Grupul a fost actualizat cu succes.',
                    confirmButtonText: 'OK'
                }).then(() => {
                    // Reîmprospătează datele pentru a vedea modificările
                    this.refreshDocumentData();

                    // Resetează starea de editare
                    this.cancelEditGroup();
                });
            },
            error: (error) => {
                console.error('Error updating group:', error);

                // Dezactivează loading
                this.isLoading = false;

                // Afișează mesaj de eroare
                Swal.fire({
                    icon: 'error',
                    title: 'Eroare',
                    text: 'Nu s-a putut actualiza grupul. Vă rugăm să încercați din nou.',
                    confirmButtonText: 'OK'
                });
            }
        });
    }

    // Anulează editarea grupului
    cancelEditGroup() {
        this.showEditGroupModal = false;
        // this.editingGroup = null;
        // this.isEditMode = false;
        // this.cancelSelection();
    }

    editDocumentGroup(group: any): void {
        console.log('Editing group:', group);

        // Setează modul editare
        this.editingGroup = group;
        this.isEditMode = true;

        // Pre-selectează paginile din grup
        this.selectedPages = [...(group.pages || [])];

        // Activează modul selecție
        this.isSelectionMode = true;

        console.log('Edit mode activated. Selected pages:', this.selectedPages);
    }

    deleteGroup(id: number) {
        Swal.fire({
            title: 'Sunteți sigur?',
            text: 'Nu o să puteți recupera datele odată cu ștergerea lor!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Confirm!',
            cancelButtonText: 'Cancel',
            reverseButtons: true,
            showLoaderOnConfirm: true,
        }).then((result) => {
            if (result.isConfirmed) {
                this.scansService.deleteGroup(id).subscribe(() => {
                    Swal.fire({
                        title: 'Șters!',
                        text: 'Grup șters cu succes.',
                        icon: 'success',
                        confirmButtonText: 'OK',
                    }).then(() => {
                        this.refreshDocumentData();
                    });
                });
            }
        });
    }

    getPageMetadata(pageNum: number): PageMetadata | undefined {
        return this.pageMetadata.find(pm => pm.pageNumber === pageNum);
    }

    get currentPageGroup(): PageGroup | undefined {
        return this.pageGroups.find(group => group.pages.includes(this.currentPage));
    }

    // Action methods - Modificat pentru a salva doar metadatele grupului
    saveAllChanges() {
        if (this.selectedDocumentGroup) {
            // Creează payload-ul pentru API
            const payload = {
                group_id: this.selectedDocumentGroup.id,
                title: this.selectedDocumentGroup.title || null,
                description: this.selectedDocumentGroup.description || null,
                notes: this.selectedDocumentGroup.notes || null,
                entity_id: this.selectedDocumentGroup.entity_id || null,
                id_document_type: this.selectedDocumentGroup.document_type_id || null,
            };

            console.log('Saving group metadata:', payload);

            // Activează loading
            this.isLoading = true;

            this.tipuriDocumentControl.setValue('');

            // Fă API call-ul pentru salvarea metadatelor
            this.scansService.storeGroupMetadata(payload).subscribe({
                next: (response) => {
                    console.log('Group metadata updated successfully:', response);

                    // Dezactivează loading
                    this.isLoading = false;

                    // Afișează mesaj de succes
                    Swal.fire({
                        icon: 'success',
                        title: 'Succes',
                        text: 'Metadatele grupului au fost salvate cu succes.',
                        confirmButtonText: 'OK'
                    }).then(() => {
                        // Reîmprospătează datele pentru a vedea modificările
                        this.refreshDocumentData();
                    });
                },
                error: (error) => {
                    console.error('Error updating group metadata:', error);

                    // Dezactivează loading
                    this.isLoading = false;

                    // Afișează mesaj de eroare
                    Swal.fire({
                        icon: 'error',
                        title: 'Eroare',
                        text: 'Nu s-au putut salva metadatele grupului. Vă rugăm să încercați din nou.',
                        confirmButtonText: 'OK'
                    });
                }
            });
        } else {
            Swal.fire({
                icon: 'warning',
                title: 'Atenție',
                text: 'Nu aveți selectat niciun grup pentru a salva metadatele.',
                confirmButtonText: 'OK'
            });
        }
    }

    downloadPdf() {
        if (this.data.pdf.blob) {
            const link = document.createElement('a');
            link.href = this.data.pdf.url;
            link.download = this.data.pdf.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            const link = document.createElement('a');
            link.href = this.data.pdf.url;
            link.download = this.data.pdf.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }

    deletePdf() {
        if (confirm(`Sigur vrei să ștergi PDF-ul "${this.data.pdf.name}"?`)) {
            console.log('Deleting PDF:', this.data.pdf);
            this.closeDialog();
        }
    }

    formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    formatDate(dateString: string): string {
        if (!dateString) return '';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();

        return `${day}.${month}.${year}`;
    }

    closeDialog() {
        this.cleanupResources();
        setTimeout(() => {
            this.dialogRef.close();
        }, 100);
    }

    forceCloseDialog() {
        try {
            this.cleanupResources();
        } catch (error) {
            console.log('Error during force cleanup:', error);
        }
        this.dialogRef.close();
    }

    // Verifică dacă există grupuri de documente
    hasDocumentGroups(): boolean {
        return this.documentGroups && this.documentGroups.length > 0;
    }

    // Verifică dacă o pagină este deja în grup
    isPageInAnyGroup(pageNum: number): boolean {
        return this.documentGroups.some(group =>
            group.pages && group.pages.includes(pageNum)
        );
    }

    // Obține paginile disponibile pentru selecție (care nu sunt în grupuri)
    getAvailablePages(): number[] {
        return this.pagesList.filter(page => !this.isPageInAnyGroup(page));
    }

    // Obține paginile din grupul curent (pentru editare)
    getCurrentGroupPages(): number[] {
        return this.editingGroup ? this.editingGroup.pages || [] : [];
    }

    // Verifică dacă o pagină poate fi selectată (nu e în alt grup SAU e în grupul curent care se editează)
    canSelectPage(pageNum: number): boolean {
        if (this.editingGroup) {
            // În modul editare, poate selecta pagini din grupul curent sau pagini libere
            const isInCurrentGroup = this.getCurrentGroupPages().includes(pageNum);
            const isInOtherGroup = this.documentGroups
                .filter(group => group.id !== this.editingGroup.id)
                .some(group => group.pages && group.pages.includes(pageNum));
            return isInCurrentGroup || !isInOtherGroup;
        } else {
            // În modul creare, poate selecta doar pagini libere
            return !this.isPageInAnyGroup(pageNum);
        }
    }

    // Inițializează numele documentului
    private initializeDocumentName(): void {
        this.originalDocumentName = this.data.rowData?.display_name || '';
        this.editingDocumentName = this.originalDocumentName;
    }

    // Deschide modalul de editare
    openEditNameModal(): void {
        this.editingDocumentName = this.data.rowData?.display_name || '';
        this.showEditNameModal = true;

        // // Focus pe input după ce modalul s-a deschis
        // setTimeout(() => {
        //     const nameInput = document.querySelector('input[matInput]') as HTMLInputElement;
        //     if (nameInput) {
        //         nameInput.focus();
        //         nameInput.setSelectionRange(nameInput.value.length, nameInput.value.length);
        //     }
        // }, 100);
    }

    // Anulează editarea
    cancelEditName(): void {
        this.showEditNameModal = false;
        this.editingDocumentName = this.originalDocumentName;
        this.isSavingName = false;
    }

    // Resetează la numele original
    resetToOriginalName(): void {
        this.editingDocumentName = this.originalDocumentName;
    }

    // Verifică dacă numele s-a schimbat
    hasNameChanged(): boolean {
        return this.editingDocumentName.trim() !== this.originalDocumentName.trim();
    }

    // Verifică dacă numele este valid
    isValidName(): boolean {
        const trimmedName = this.editingDocumentName.trim();
        return trimmedName.length > 0 &&
            trimmedName.length <= 255 &&
            /^[a-zA-Z0-9\s\-_.,()[\]{}!@#$%^&*+=|\\:;'"<>?/~`]+$/.test(trimmedName);
    }

    // Salvează numele documentului
    saveDocumentName(): void {
        if (!this.hasNameChanged() || !this.isValidName() || this.isSavingName) {
            return;
        }

        const trimmedName = this.editingDocumentName.trim();

        // Confirmă modificarea
        Swal.fire({
            title: 'Confirmi modificarea?',
            text: `Numele documentului va fi schimbat în: "${trimmedName}"`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#2196f3',
            cancelButtonColor: '#757575',
            confirmButtonText: 'Da, salvează',
            cancelButtonText: 'Anulează',
            reverseButtons: true
        }).then((result) => {
            if (result.isConfirmed) {
                this.performNameUpdate(trimmedName);
            }
        });
    }

    // Execută actualizarea numelui
    private performNameUpdate(newName: string): void {
        this.isSavingName = true;

        const payload = {
            document_id: this.data.rowData.id,
            name: newName
        };

        console.log('Updating document name:', payload);

        this.scansService.updateDocumentName(payload).subscribe({
            next: (response) => {
                console.log('Document name updated successfully:', response);

                // Actualizează datele locale
                this.data.rowData.display_name = newName;
                this.originalDocumentName = newName;
                this.editingDocumentName = newName;

                // Închide modalul
                this.showEditNameModal = false;
                this.isSavingName = false;

                // Afișează mesaj de succes
                Swal.fire({
                    icon: 'success',
                    title: 'Succes!',
                    text: 'Numele documentului a fost actualizat cu succes.',
                    timer: 3000,
                    timerProgressBar: true,
                    showConfirmButton: false,
                    toast: true,
                    position: 'top-end'
                });

                // Actualizează și titlul din header
                this.refreshDocumentData();
            },
            error: (error) => {
                console.error('Error updating document name:', error);
                this.isSavingName = false;

                // Afișează mesajul de eroare
                Swal.fire({
                    icon: 'error',
                    title: 'Eroare',
                    text: 'Nu s-a putut actualiza numele documentului. Vă rugăm să încercați din nou.',
                    confirmButtonText: 'OK'
                });
            }
        });
    }

    // Funcție helper pentru validarea numelui (cu mesaj de eroare)
    getNameValidationMessage(): string {
        const trimmedName = this.editingDocumentName.trim();

        if (trimmedName.length === 0) {
            return 'Numele nu poate fi gol.';
        }

        if (trimmedName.length > 255) {
            return 'Numele nu poate avea mai mult de 255 de caractere.';
        }

        if (!/^[a-zA-Z0-9\s\-_.,()[\]{}!@#$%^&*+=|\\:;'"<>?/~`]+$/.test(trimmedName)) {
            return 'Numele conține caractere nevalide.';
        }

        return '';
    }

    // Event handler pentru Escape key
    @HostListener('document:keydown.escape', ['$event'])
    onEscapeKey(event: KeyboardEvent): void {
        if (this.showEditNameModal) {
            event.preventDefault();
            this.cancelEditName();
        }
    }

    // Event handler pentru click pe overlay
    onOverlayClick(event: MouseEvent): void {
        if (event.target === event.currentTarget) {
            this.cancelEditName();
        }
    }

    renderFieldsSearch() {
        this.entitatiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredEntitati = this.entitiesList;
                    return of(this.entitiesList);
                }
                return this.filterEntitati(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredEntitati = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.tipuriDocumentControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredTipuriDocument = this.documentTypes;
                    return of(this.documentTypes);
                }
                return this.filterTipuriDocument(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredTipuriDocument = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.entitatiGrupControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredEntitatiGrup = this.entitiesList;
                    return of(this.entitiesList);
                }
                return this.filterEntitati(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredEntitatiGrup = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.operatoriControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredOperatori = this.operatori;
                    return of(this.operatori);
                }
                return this.filterOperatori(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredOperatori = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterEntitati(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.entitiesList.filter(entitate =>
            this.normalizeString(entitate.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onEntitatiSelectClosed() {
        const selectedValue = this.entitatiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredEntitati.some(entitate =>
                entitate.id === this.selectedEntityId
            );

            if (!isSelectedInFilteredList) {
                this.entitatiControl.setValue(null);
                this.filteredEntitati = this.entitiesList;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    private resetGrupSearchControl(): void {
        // Simplu resetează controlul și lista filtrată
        this.entitatiGrupControl.setValue('', { emitEvent: false });
        this.filteredEntitatiGrup = this.entitiesList;
    }

    onEntitatiGrupSelectClosed() {
        const selectedValue = this.entitatiGrupControl.value;

        if (selectedValue && this.selectedDocumentGroup?.entity_id) {
            const isSelectedInFilteredList = this.filteredEntitatiGrup.some(entitate =>
                entitate.id === this.selectedDocumentGroup.entity_id
            );

            if (!isSelectedInFilteredList) {
                this.entitatiGrupControl.setValue('');
                this.filteredEntitatiGrup = this.entitiesList;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    filterTipuriDocument(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.documentTypes.filter(tipDocument =>
            this.normalizeString(tipDocument.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onTipuriDocumentSelectClosed() {
        const selectedValue = this.tipuriDocumentControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredTipuriDocument.some(tipDocument =>
                tipDocument.id === this.selectedDocumentGroup.document_type_id
            );

            if (!isSelectedInFilteredList) {
                this.tipuriDocumentControl.setValue(null);
                this.filteredTipuriDocument = this.documentTypes;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    filterOperatori(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.operatori.filter(operator =>
            this.normalizeString(operator.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onOperatorAsignatSelectClosed() {
        const selectedValue = this.operatoriControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredOperatori.some(operator =>
                operator.id === this.selectedOperator
            );

            if (!isSelectedInFilteredList) {
                this.operatoriControl.setValue(null);
                this.filteredOperatori = this.operatori;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    loadDocumentTypes() {
        this.documentTypeService.getListActive().subscribe({
            next: (response: any) => {
                this.documentTypes = response.data;
                this.filteredTipuriDocument = response.data;
                console.log('Document types loaded:', this.documentTypes);
            },
            error: (error: any) => {
                console.error('Error loading document types:', error);
            }
        });
    }

    getNonFinalizedGroups(): any[] {
        return this.documentGroups.filter(group => !group.is_finalized);
    }

    // Obține grupurile finalizate
    getFinalizedGroups(): any[] {
        return this.documentGroups.filter(group => group.is_finalized);
    }

    // Verifică dacă există grupuri active (nefinalizate)
    hasActiveDocumentGroups(): boolean {
        return this.getNonFinalizedGroups().length > 0;
    }

    // Finalizează un grup
    finalizeGroup(group: any): void {
        Swal.fire({
            title: 'Finalizare Grup',
            text: `Sigur vreți să finalizați grupul "${group.title || 'Grup ' + group.id}"?`,
            html: `
            <div style="text-align: left; margin-top: 16px;">
                <p><strong>Grupul:</strong> ${group.title || 'Grup ' + group.id}</p>
                <p><strong>Pagini:</strong> ${group.total_pages_in_group}</p>
                <p style="color: #ff9800; font-weight: 500; margin-top: 12px;">
                    ⚠️ Odată finalizat, grupul nu va mai putea fi editat sau șters și nu va mai apărea în lista de metadate.
                </p>
            </div>
        `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4caf50',
            cancelButtonColor: '#757575',
            confirmButtonText: 'Da, finalizează',
            cancelButtonText: 'Anulează',
            reverseButtons: true,
            customClass: {
                popup: 'finalize-group-popup'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                this.performGroupFinalization(group);
            }
        });
    }

    // Execută finalizarea grupului
    private performGroupFinalization(group: any): void {
        this.isLoading = true;

        this.scansService.finalizeGroup(group.id).subscribe({
            next: (response) => {
                console.log('Group finalized successfully:', response);

                // Actualizează grupul local
                group.is_finalized = true;

                // Dacă grupul finalizat era selectat în metadate, resetează selecția
                if (this.selectedDocumentGroup && this.selectedDocumentGroup.id === group.id) {
                    this.selectedDocumentGroup = null;
                }

                this.isLoading = false;

                // Afișează mesaj de succes
                Swal.fire({
                    icon: 'success',
                    title: 'Grup Finalizat',
                    text: `Grupul "${group.title || 'Grup ' + group.id}" a fost finalizat cu succes.`,
                    timer: 3000,
                    timerProgressBar: true,
                    showConfirmButton: false,
                    toast: true,
                    position: 'top-end'
                });

                // Reîmprospătează datele pentru a sincroniza cu serverul
                this.refreshDocumentData();
            },
            error: (error) => {
                console.error('Error finalizing group:', error);
                this.isLoading = false;

                Swal.fire({
                    icon: 'error',
                    title: 'Eroare',
                    text: 'Nu s-a putut finaliza grupul. Vă rugăm să încercați din nou.',
                    confirmButtonText: 'OK'
                });
            }
        });
    }

    // Reactivează un grup finalizat
    resetGroupFinalization(group: any): void {
        Swal.fire({
            title: 'Reactivare Grup',
            text: `Sigur vreți să reactivați grupul "${group.title || 'Grup ' + group.id}"?`,
            html: `
            <div style="text-align: left; margin-top: 16px;">
                <p><strong>Grupul:</strong> ${group.title || 'Grup ' + group.id}</p>
                <p><strong>Pagini:</strong> ${group.total_pages_in_group}</p>
                <p style="color: #2196f3; font-weight: 500; margin-top: 12px;">
                    ℹ️ Odată reactivat, grupul va deveni din nou editabil și va apărea în lista de metadate.
                </p>
            </div>
        `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#2196f3',
            cancelButtonColor: '#757575',
            confirmButtonText: 'Da, reactivează',
            cancelButtonText: 'Anulează',
            reverseButtons: true,
            customClass: {
                popup: 'revert-group-popup'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                this.performGroupRevert(group);
            }
        });
    }

    // Execută reactivarea grupului
    private performGroupRevert(group: any): void {
        this.isLoading = true;

        this.scansService.resetFinalizeGroup(group.id).subscribe({
            next: (response) => {
                console.log('Group revert successful:', response);

                // Actualizează grupul local
                group.is_finalized = false;

                this.isLoading = false;

                // Afișează mesaj de succes
                Swal.fire({
                    icon: 'success',
                    title: 'Grup Reactivat',
                    text: `Grupul "${group.title || 'Grup ' + group.id}" a fost reactivat cu succes.`,
                    timer: 3000,
                    timerProgressBar: true,
                    showConfirmButton: false,
                    toast: true,
                    position: 'top-end'
                });

                // Reîmprospătează datele pentru a sincroniza cu serverul
                this.refreshDocumentData();
            },
            error: (error) => {
                console.error('Error reverting group finalization:', error);
                this.isLoading = false;

                Swal.fire({
                    icon: 'error',
                    title: 'Eroare',
                    text: 'Nu s-a putut reactiva grupul. Vă rugăm să încercați din nou.',
                    confirmButtonText: 'OK'
                });
            }
        });
    }

    onGroupExpanded(group: any, isExpanded: boolean): void {
        if (isExpanded) {
            this.expandedGroupId = group.id;
            console.log('Group expanded:', group.id, 'Pages:', group.pages);
        } else {
            this.expandedGroupId = null;
            console.log('Group collapsed');
        }
    }

    // Adaugă această metodă pentru a verifica dacă o pagină este în grupul expandat
    isPageInExpandedGroup(pageNum: number): boolean {
        if (!this.expandedGroupId) return false;

        const expandedGroup = this.documentGroups.find(group => group.id === this.expandedGroupId);
        return expandedGroup ? (expandedGroup.pages || []).includes(pageNum) : false;
    }

    // Adaugă această metodă pentru a obține grupul expandat
    getExpandedGroup(): any {
        return this.expandedGroupId ?
            this.documentGroups.find(group => group.id === this.expandedGroupId) :
            null;
    }
}